import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LogoShieldIcon,
  NavOrganizationsIcon,
  NavRolesIcon,
  DirectoryIcon,
  StakeholderIcon,
  HierarchyNodesIcon,
  PublicAdminIcon,
  ResourceDirectoryIcon,
  CaretIcon,
} from '../../assets/icons';
import type { FunctionComponent, SVGProps } from 'react';

interface NavItem {
  to: string;
  label: string;
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
}

/** Expandable "Build Hierarchy" group — administrative-level configuration pages. */
const BUILD_HIERARCHY_LINKS: { to: string; label: string }[] = [
  { to: '/admin/build-hierarchy/party', label: 'Party' },
  { to: '/admin/build-hierarchy/state-ec', label: 'State Police' },
  { to: '/admin/build-hierarchy/state-government', label: 'State Revenue' },
];

/** Expandable "Directory" group — each sub-item is its own directory-scoped Users page. */
const DIRECTORY_LINKS: { to: string; label: string }[] = [
  { to: '/admin/users', label: 'All Person' },
  { to: '/admin/users/political', label: 'Political Person' },
  { to: '/admin/users/government-official', label: 'Government Official' },
  { to: '/admin/users/party', label: 'Party Person' },
  { to: '/admin/users/client-team', label: 'Client Team User' },
  { to: '/admin/users/others', label: 'Others' },
];

// Only these accounts see the ECI Data Pull menu (backend also enforces it).
const DATA_PANEL_EMAILS = ['nasib@oogw.ai'];

function currentEmail(): string {
  try {
    return JSON.parse(localStorage.getItem('chunaav.auth') || '{}')?.email || '';
  } catch {
    return '';
  }
}

/** Shared classes for a top-level navigation link. */
const topLinkClass = (isActive: boolean) =>
  [
    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium no-underline transition-colors',
    isActive
      ? 'bg-brand-shade text-brand'
      : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
  ].join(' ');

/** An expandable navigation group with a header row and indented sub-links. */
function NavGroup({
  label,
  Icon,
  links,
  activePrefix,
  exactBase,
  onClose,
}: {
  label: string;
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  links: { to: string; label: string }[];
  /** The group header highlights + auto-expands while a route under this prefix is open. */
  activePrefix: string;
  /** A sub-link that must match its path exactly (e.g. /admin/users vs its sub-routes). */
  exactBase?: string;
  onClose?: () => void;
}) {
  const { pathname } = useLocation();
  const active = pathname.startsWith(activePrefix);
  const [open, setOpen] = useState(active);
  // Auto-open the group when navigating into one of its routes.
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={[
          'flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium transition-colors',
          active ? 'text-brand' : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
        ].join(' ')}
      >
        <span className="flex items-center gap-3">
          <Icon className="size-5 shrink-0" />
          <span className="leading-5">{label}</span>
        </span>
        <CaretIcon
          className={['size-3.5 shrink-0 transition-transform', open ? '' : '-rotate-90'].join(' ')}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-1 pl-12">
          {links.map(({ to, label: linkLabel }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              end={to === exactBase}
              className={({ isActive }) =>
                [
                  'rounded-lg px-4 py-2 text-sm font-medium no-underline transition-colors',
                  isActive
                    ? 'bg-brand-shade text-brand'
                    : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
                ].join(' ')
              }
            >
              {linkLabel}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
}

interface SidebarProps {
  /** Whether the mobile drawer is open (ignored on desktop, where it's always visible). */
  open?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ open = false, onClose }: SidebarProps) {
  const topItems: NavItem[] = [
    { to: '/admin/organizations', label: 'Organization', Icon: NavOrganizationsIcon },
  ];
  const bottomItems: NavItem[] = [
    { to: '/admin/teams', label: 'Team', Icon: StakeholderIcon },
    { to: '/admin/roles', label: 'Roles and Permission', Icon: NavRolesIcon },
  ];
  if (DATA_PANEL_EMAILS.includes(currentEmail())) {
    bottomItems.push({ to: '/admin/data-pull', label: 'Electoral Data', Icon: NavOrganizationsIcon });
  }

  return (
    <>
      {/* Backdrop — mobile only, when the drawer is open */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-16 z-20 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={[
          'fixed left-0 top-16 z-30 flex h-[calc(100vh-64px)] w-[280px] flex-col gap-6 overflow-y-auto border-r border-line-soft bg-white pl-4 pr-[17px] py-4 transition-transform duration-200 lg:z-10 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Admin profile card */}
        <div className="flex items-center gap-3 rounded-xl bg-brand-shade p-4">
          <span className="flex items-center justify-center rounded-lg bg-brand p-2 text-white">
            <LogoShieldIcon className="size-5" />
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-bold leading-4 text-ink">CHUNAAV Admin</span>
            <span className="text-[10px] font-medium uppercase leading-[15px] tracking-[0.5px] text-brand">
              System Administrator
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1">
          {topItems.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={onClose} className={({ isActive }) => topLinkClass(isActive)}>
              <Icon className="size-5 shrink-0" />
              <span className="leading-5">{label}</span>
            </NavLink>
          ))}

          {/* Build Hierarchy — expandable group */}
          <NavGroup
            label="Build Hierarchy"
            Icon={HierarchyNodesIcon}
            links={BUILD_HIERARCHY_LINKS}
            activePrefix="/admin/build-hierarchy"
            onClose={onClose}
          />

          {/* Dept. Configuration — create government departments + designation ranks */}
          <NavLink
            to="/admin/dept-configuration"
            onClick={onClose}
            className={({ isActive }) => topLinkClass(isActive)}
          >
            <PublicAdminIcon className="size-5 shrink-0" />
            <span className="leading-5">Dept. Configuration</span>
          </NavLink>

          {/* Directory — expandable group */}
          <NavGroup
            label="Directory"
            Icon={DirectoryIcon}
            links={DIRECTORY_LINKS}
            activePrefix="/admin/users"
            exactBase="/admin/users"
            onClose={onClose}
          />

          {/* Resources Directory — constituency resources (schools, hospitals, …) */}
          <NavLink
            to="/admin/resource-directory"
            onClick={onClose}
            className={({ isActive }) => topLinkClass(isActive)}
          >
            <ResourceDirectoryIcon className="size-5 shrink-0" />
            <span className="leading-5">Resources Directory</span>
          </NavLink>

          {bottomItems.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={onClose} className={({ isActive }) => topLinkClass(isActive)}>
              <Icon className="size-5 shrink-0" />
              <span className="leading-5">{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
